import { createSlice } from "@reduxjs/toolkit";
import imageCompression from "browser-image-compression";
import { factory } from "../../api/apiFactory";
import { showNotification } from "./notificationSlice";

const api = factory.get("ticket_quality");

const initialState = {
  list: {
    loading: false,
    data: [],
    total: 0,
    filters: {
      skip: 0,
      take: 25,
      page: 0,
      search: "",
      responsible_id: null,
      status_id: {
        id: 1,
        name: "جديد",
      },
    },
    filters_dialog: false,
  },
  dialog: false,
  form: {
    title: "",
    ftth_ticket_id: "",
    description: "",
    responsible_id: null,
    poles_ids: [],
    cabinet_ids: [],
    attachment: [],
  },
  statuses: {
    data: [],
    loading: false,
  },
  details: {
    data: {
      id: null,
      title: "",
      description: "",
      status: "",
      responsible_id: null,
      responsible_name: null,
      cabinet_ids: [],
      poles_ids: [],
      replies: [],
    },
    loading: false,
  },
  reply: {
    progress: 0,
    form: {
      ticket_quality_id: "",
      description: "",
      attachment: [],
      video: null,
    },
    loading: false,
    dialog: false,
  },
  responsible: {
    loading: false,
    dialog: false,
    form: {
      responsible_id: null,
      ticket_quality_id: null,
    },
  },
};

export const ticketQualitySlice = createSlice({
  name: "ticketQuality",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state[action.payload].loading = !state[action.payload].loading;
    },
    setList: (state, action) => {
      state.list.data = action.payload.data.data;
      state.list.total = action.payload.data.total;
    },
    setFiltersDialog: (state) => {
      state.list.filters_dialog = !state.list.filters_dialog;
    },
    setFilters: (state, { payload }) => {
      state.list.filters = {
        ...state.list.filters,
        ...payload,
      };
    },
    resetFilters: (state) => {
      state.list.filters = initialState.list.filters;
    },
    setDialog: (state) => {
      state.dialog = !state.dialog;
      if (!state.dialog) {
        state.form = initialState.form;
      }
    },
    setForm: (state, { payload }) => {
      state.form = { ...state.form, ...payload };
    },
    setStatuses: (state, { payload }) => {
      state.statuses.data = payload.data.data;
      state.statuses.loading = false;
    },
    setDetails: (state, { payload }) => {
      state.details.data = payload.data?.data[0] || 0
    },
    resetDetails: (state) => {
      state.details.data = initialState.details.data;
    },
    setReplyDialog: (state, { payload }) => {
      state.reply.dialog = !state.reply.dialog;
      if (payload?.id) {
        state.reply.form.ticket_quality_id = payload.id;
      }
      if (!state.reply.dialog) {
        state.reply.form = initialState.reply.form;
        state.reply.progress = 0;
      }
    },
    setReplyProgress: (state, { payload }) => {
      state.reply.progress = payload;
    },
    setResponsibleDialog: (state, { payload }) => {
      state.responsible.dialog = !state.responsible.dialog;
      if (payload?.id) {
        state.responsible.form.ticket_quality_id = payload.id;
        state.responsible.form.responsible_id = payload.responsible_id || null;
      }
      if (!state.responsible.dialog) {
        state.responsible.form = initialState.responsible.form;
      }
    },
  },
});

export const {
  setLoading,
  setList,
  setFiltersDialog,
  setFilters,
  resetFilters,
  setDialog,
  setForm,
  setStatuses,
  setDetails,
  resetDetails,
  setReplyDialog,
  setReplyProgress,
  setResponsibleDialog,
} = ticketQualitySlice.actions;

export default ticketQualitySlice.reducer;

export const getAll = (params = null) => async (dispatch, getState) => {
  try {
    const filters = getState().ticketQuality.list.filters;
    const filters_ = {
      ...filters,
      responsible_id: filters.responsible_id?.id || null,
      status_id: filters.status_id?.id || null,
      ...params,  
    };
    dispatch(setLoading("list"));
    const res = await api.getAll(filters_);
    dispatch(setList(res));
    dispatch(setLoading("list"));
  } catch (err) {
    dispatch(setLoading("list"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "حدث خطأ ما",
      })
    );
  }
};

export const getStatuses = () => async (dispatch) => {
  try {
    dispatch(setLoading("statuses"));
    const res = await api.statuses();
    dispatch(setStatuses(res));
  } catch (err) {
    dispatch(setLoading("statuses"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "حدث خطأ ما",
      })
    );
  }
};

export const create = (data) => async (dispatch) => {
  try {
    dispatch(setLoading("list"));
    const formData = new FormData();
    formData.append("title", data.title);
    if (data.ftth_ticket_id) formData.append("ftth_ticket_id", data.ftth_ticket_id || "");
    formData.append("description", data.description);
    formData.append("responsible_id", data.responsible_id?.id || "");

    if (data.poles_ids && data.poles_ids.length > 0) {
      data.poles_ids.forEach((item) => formData.append("poles_ids[]", item.id || item));
    }
    if (data.cabinet_ids && data.cabinet_ids.length > 0) {
      data.cabinet_ids.forEach((item) => formData.append("cabinet_ids[]", item.id || item));
    }

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
    };

    if (data.attachment && data.attachment.length > 0) {
      let files = data.attachment;
      for (let i = 0; i < files.length; i++) {
        const compressedFile = await imageCompression(files[i].file, options);
        formData.append(`attachment[${i}]`, compressedFile);
      }
    }
    await api.create(formData);
    dispatch(
      showNotification({
        type: "success",
        message: "تم الاضافة بنجاح",
      })
    );
    dispatch(setDialog());
    dispatch(setLoading("list"));
    dispatch(getAll());
    return true;
  } catch (err) {
    dispatch(setLoading("list"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "حدث خطأ ما",
      })
    );
    return false;
  }
};

export const showById = (id) => async (dispatch) => {
  try {
    dispatch(setLoading("details"));
    const res = await api.showById(id);
    dispatch(setDetails(res));
    dispatch(setLoading("details"));
  } catch (err) {
    dispatch(setLoading("details"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "حدث خطأ ما",
      })
    );
  }
};

export const reply = (data) => async (dispatch) => {
  try {
    dispatch(setLoading("reply"));
    const formData = new FormData();
    formData.append("ticket_quality_id", data.ticket_quality_id);
    formData.append("description", data.description);

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
    };

    if (data.attachment && data.attachment.length > 0) {
      let files = data.attachment;
      for (let i = 0; i < files.length; i++) {
        const compressedFile = await imageCompression(files[i].file, options);
        formData.append(`attachment[${i}]`, compressedFile);
      }
    }

    if (data.video) {
      formData.append("video", data.video);
    }

 

    await api.reply(formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent;
        const percent = Math.floor((loaded * 100) / total);
        dispatch(setReplyProgress(percent));
      },
    });
    dispatch(
      showNotification({
        type: "success",
        message: "تم الرد بنجاح",
      })
    );
    dispatch(setLoading("reply"));
    dispatch(setReplyDialog());
    dispatch(showById(data.ticket_quality_id));
    return true;
  } catch (err) {
    dispatch(setReplyProgress(0));
    dispatch(setLoading("reply"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "حدث خطأ ما",
      })
    );
  }
};

export const changeStatus = (payload) => async (dispatch) => {
  try {
    dispatch(setLoading("list"));
    await api.updateStatus(payload.id, payload.status_id);
    dispatch(
      showNotification({
        type: "success",
        message: "تم تحديث الحالة بنجاح",
      })
    );
    dispatch(setLoading("list"));
    dispatch(getAll());
    if (payload.from === "details") {
       dispatch(showById(payload.id));
    }
    return true;
  } catch (err) {
    dispatch(setLoading("list"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "حدث خطأ ما",
      })
    );
    return false;
  }
};

export const assignResponsible = (data) => async (dispatch) => {
  try {
    dispatch(setLoading("responsible"));
    await api.updateResponsible(data.ticket_quality_id, {
      responsible_id: data.responsible_id?.id || data.responsible_id || null,
    });
    dispatch(
      showNotification({
        type: "success",
        message: "تم تعيين المسؤول بنجاح",
      })
    );
    dispatch(setLoading("responsible"));
    dispatch(setResponsibleDialog());
    
    // Refetch the list & active details
    dispatch(getAll());
    dispatch(showById(data.ticket_quality_id));
    return true;
  } catch (err) {
    dispatch(setLoading("responsible"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "حدث خطأ ما",
      })
    );
    return false;
  }
};
